<template>
  <Drawer
    :visible="visible"
    position="right"
    class="overview-milestone-drawer"
    :style="{ width: '30rem' }"
    data-testid="overview-milestone-drawer"
    @update:visible="$emit('update:visible', $event)"
  >
    <template #header>
      <div class="flex items-center gap-2 min-w-0 flex-1">
        <Tag :value="statusLabel" :severity="statusSeverity" class="text-[10px] uppercase shrink-0" />
        <h2 class="text-lg font-semibold text-[var(--color-dark)] m-0 truncate">
          {{ milestone?.title || '—' }}
        </h2>
      </div>
    </template>

    <div v-if="milestone" class="flex flex-col gap-5">
      <div class="overview-milestone-drawer__info grid grid-cols-2 gap-4">
        <div>
          <p class="text-xs text-[var(--color-disabled)] m-0 mb-1">
            {{ $t('UserProjects.overview.milestoneDrawer.dueDate') }}
          </p>
          <p class="text-sm font-semibold text-[var(--color-dark)] m-0">
            {{ formatLongDate(milestone.dueDate) }}
          </p>
        </div>
        <div>
          <p class="text-xs text-[var(--color-disabled)] m-0 mb-1">
            {{ $t('UserProjects.overview.milestoneDrawer.status') }}
          </p>
          <p class="text-sm font-semibold text-[var(--color-dark)] m-0">
            {{ statusLabel }}
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-3">
        <h3 class="text-sm font-semibold text-[var(--color-dark)] m-0">
          {{ $t('UserProjects.overview.milestoneDrawer.linkedCommitments') }}
        </h3>
        <div v-if="loading" class="text-sm text-[var(--color-disabled)]">
          {{ $t('UserProjects.overview.loading') }}
        </div>
        <div v-else-if="linkedCommitments.length === 0" class="text-sm text-[var(--color-disabled)]">
          {{ $t('UserProjects.overview.milestoneDrawer.noCommitments') }}
        </div>
        <ul v-else class="list-none m-0 p-0 flex flex-col gap-2">
          <li
            v-for="item in linkedCommitments"
            :key="item.id"
            class="text-sm border border-surface-200 rounded-lg p-3"
          >
            <span class="font-medium text-[var(--color-dark)]">{{ item.was }}</span>
            <span class="block text-xs text-[var(--color-disabled)] mt-1">
              {{ formatLongDate(item.dueDate) }}
            </span>
          </li>
        </ul>
      </div>

      <div class="flex flex-wrap gap-2">
        <Button
          type="button"
          :label="$t('UserProjects.overview.hero.createCommitment')"
          icon="pi pi-plus"
          class="btn-primary-accent"
          unstyled
          data-testid="overview-milestone-drawer-add-commitment"
          @click="goCreateCommitment"
        />
        <Button
          type="button"
          :label="$t('UserProjects.overview.milestones.viewAll')"
          severity="secondary"
          outlined
          @click="goMilestones"
        />
      </div>
    </div>
  </Drawer>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Drawer from 'primevue/drawer';
import Button from 'primevue/button';
import Tag from 'primevue/tag';

export default {
  name: 'OverviewMilestoneDrawer',
  components: {
    Drawer,
    Button,
    Tag,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    milestone: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:visible'],
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    ...mapGetters('pmCommitment', {
      commitments: 'commitments',
    }),
    linkedCommitments() {
      if (!this.milestone?.id) return [];
      const milestoneId = String(this.milestone.id);
      return (this.commitments || []).filter(
        (item) => String(item.milestoneId) === milestoneId && item.status === 'open',
      );
    },
    statusLabel() {
      if (!this.milestone) return '';
      if (this.milestone.isOverdue && this.milestone.status !== 'done') {
        return this.$t('ProjectManagement.milestones.status.overdue');
      }
      return this.$t(`ProjectManagement.milestones.status.${this.milestone.status}`);
    },
    statusSeverity() {
      if (!this.milestone) return 'secondary';
      if (this.milestone.status === 'done') return 'success';
      if (this.milestone.isOverdue) return 'danger';
      return 'warn';
    },
  },
  watch: {
    visible(isOpen) {
      if (isOpen && this.milestone) {
        this.loadCommitments();
      }
    },
  },
  methods: {
    ...mapActions('pmCommitment', ['fetchCommitments']),
    async loadCommitments() {
      this.loading = true;
      try {
        await this.fetchCommitments();
      } catch {
        // store handles error
      } finally {
        this.loading = false;
      }
    },
    formatLongDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    },
    goCreateCommitment() {
      this.$emit('update:visible', false);
      this.$router.push({ path: '/user-projects/my-commitments', query: { create: '1' } });
    },
    goMilestones() {
      this.$emit('update:visible', false);
      this.$router.push('/user-projects/milestones');
    },
  },
};
</script>

<style scoped>
.overview-milestone-drawer__info {
  background: var(--surface-ground, #f9f9f9);
  border-radius: 10px;
  padding: 1rem;
}
</style>
